import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import ReactDOM from "react-dom";
import { useLocation } from "react-router-dom";
import {
  FileText, Plus, X, Loader2, Copy, Trash2, Download,
  Send, Eye, Edit3, BookOpen, Upload, Search,
  CheckCircle2, XCircle, Clock, RefreshCw, Percent,
  Building2, Hammer, FileSpreadsheet, ChevronRight, ChevronDown,
  AlertTriangle, History, ArrowRight, ListChecks,
  Ban, CalendarDays, Paperclip, FilePlus, Archive,
  RotateCcw, Users, TrendingUp, Target,
  Sparkles, BarChart3, PieChart as PieChartIcon, Wallet
} from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, Legend } from "recharts";
import axios from "axios";
import { API, useAuth } from "../../contexts/AuthContext";
import { formatPYG } from "../../utils/format";
import { Skeleton } from "../../components/ui/skeleton";
import { toast } from "sonner";
import { WORKFLOW_STEPS, STATUS_META, NEXT_STEP_LABEL, TIPO_META, UNIDADES, DEFAULT_PCT, opts, fmt } from "./constants";
import { Modal, DeadlineBadge, WorkflowStepper, daysUntil, getWfIndex } from "./SharedComponents";

export function ImportPreviewModal({ open, onClose, onConfirm, onAppend, data }) {
  if (!open || !data) return null;
  const items = data.items || [];
  const groups = data.groups || 0;
  const count = data.count || 0;
  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="import-preview-modal">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="text-base font-bold text-gray-900">Vista previa — Importación con IA</h2>
            <p className="text-xs text-gray-500 mt-0.5">{groups} secciones + {count} ítems detectados. Hoja: "{data.sheet_used}"</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100"><X size={16} className="text-gray-400" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-3">
          <table className="w-full text-xs">
            <thead className="bg-[#2E6DA4] text-white sticky top-0">
              <tr>
                <th className="px-2 py-2 text-left w-16">N°</th>
                <th className="px-2 py-2 text-left">Descripción</th>
                <th className="px-2 py-2 text-center w-16">Un.</th>
                <th className="px-2 py-2 text-right w-16">Cant.</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => (
                item.is_group ? (
                  <tr key={i} className="bg-[#D9EAF8]">
                    <td className="px-2 py-1.5 font-bold text-[#1A4B7A]">{item.numero}</td>
                    <td className="px-2 py-1.5 font-bold text-[#1A4B7A]" colSpan={3}>{item.descripcion}</td>
                  </tr>
                ) : (
                  <tr key={i} className={`border-b border-gray-50 ${i % 2 === 0 ? "" : "bg-[#F8FAFB]"}`}>
                    <td className="px-2 py-1 text-gray-400">{item.numero}</td>
                    <td className="px-2 py-1 text-gray-800">{item.descripcion}</td>
                    <td className="px-2 py-1 text-center text-gray-500">{item.unidad}</td>
                    <td className="px-2 py-1 text-right text-gray-700">{item.cantidad}</td>
                  </tr>
                )
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-4 border-t border-gray-100 flex gap-3 flex-shrink-0 bg-gray-50">
          <button onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-2 text-sm font-medium hover:bg-gray-50">Cancelar</button>
          {/* Fix ET (2026-10-01): agregar al final sin borrar ni reemplazar lo cargado */}
          {onAppend && (
            <button onClick={() => onAppend(items)}
              className="flex-1 border border-[#2E6DA4] text-[#2E6DA4] hover:bg-[#EBF2F9] rounded-xl py-2 text-sm font-semibold transition-colors flex items-center justify-center gap-2" data-testid="confirm-append-btn">
              <FilePlus size={15} /> Agregar al final ({count} ítems)
            </button>
          )}
          <button onClick={() => onConfirm(items)}
            className="flex-1 bg-[#2E6DA4] hover:bg-[#245A8A] text-white rounded-xl py-2 text-sm font-semibold transition-colors flex items-center justify-center gap-2" data-testid="confirm-import-btn">
            <CheckCircle2 size={15} /> Confirmar e importar {count} ítems
          </button>
        </div>
      </div>
    </div>
  , document.body);
}
